import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DOCUMENT_TYPE_OPTIONS } from './document-type-groups';
import {
  SUPPORTED_EXTENSIONS,
  UNSUPPORTED_TYPE_MESSAGE,
  fileExtension,
} from './document-file-types';

// Default title: the file name without its extension, nothing more. It stays
// editable per row; no separate manual naming step is required.
function titleFromFileName(fileName) {
  const extension = fileExtension(fileName);
  return extension ? fileName.slice(0, -extension.length) : fileName;
}

// One row per file, ids counting up from firstId. The same rows whether the
// files were chosen with "Add files" or dropped on a Documents section.
function rowsForFiles(files, documentTypeId, firstId) {
  return files.map((file, index) => {
    const supported = SUPPORTED_EXTENSIONS.includes(fileExtension(file.name));
    return {
      id: firstId + index,
      file,
      title: titleFromFileName(file.name),
      documentTypeId,
      status: 'idle',
      invalid: !supported,
      errorMessage: supported ? '' : UNSUPPORTED_TYPE_MESSAGE,
    };
  });
}

function isRowEligibleToUpload(row) {
  return (
    !row.invalid &&
    row.title.trim().length > 0 &&
    row.status !== 'uploading' &&
    row.status !== 'success'
  );
}

/**
 * The one Upload dialog of the manual-upload workflow, for one file or many.
 * Every file goes through the single-document API call
 * (DocumentsAPI.uploadDocument, via the parent's onUploadAll) -- this
 * component adds client-side batching (multi-select, per-row editing,
 * bounded-concurrency orchestration owned by the parent, per-row status) on
 * top of it. No batch backend endpoint, no duplicated document-type mapping
 * (DOCUMENT_TYPE_OPTIONS is imported, never redefined).
 *
 * One discipline is chosen for the WHOLE batch (not per-row) -- every
 * document belongs to exactly one real discipline, and there is no
 * project-level discipline selection for this dialog to inherit. "Upload
 * All" is disabled until a discipline is chosen.
 *
 * "Upload All" only ever attempts rows that are not already uploading or
 * succeeded (see isRowEligibleToUpload) -- clicking it again after a
 * partial failure IS the retry mechanism: previously-failed rows are
 * retried, previously-succeeded rows are never re-submitted (which would
 * create a duplicate document server-side).
 *
 * The dialog closes itself only when an Upload All leaves EVERY row
 * uploaded: each attempted file reported success and every row it did not
 * attempt had already succeeded. Any failed, unsupported, or untitled row
 * keeps it open, so nothing the user still needs to see or retry vanishes.
 *
 * Document TYPE, unlike discipline, still defaults per-row and stays
 * individually editable (see the per-row Type Select below) -- but
 * changing every row one at a time is tedious for a large batch that's
 * genuinely all one type (e.g. a folder of drawings), so `bulkTypeId` +
 * handleBulkTypeChange offer a one-shot "assign this type to every row
 * currently in the batch" action. `bulkTypeId` doubles as the DEFAULT type
 * for any file added afterward -- the least-surprising choice: once the
 * user has told this batch "these are all Specifications", a file dropped
 * in a moment later should start out a Specification too, not silently
 * revert to the hardcoded first option. A row's own Type Select can still
 * be changed individually at any time, before or after a bulk assignment --
 * this action only sets each row's CURRENT value, it does not lock rows to
 * the bulk choice.
 *
 * Files dropped on a Documents section arrive as `initialFiles` with that
 * section's `initialDocumentTypeId`: they start as rows of that type, which
 * is also the bulk type, and are then handled exactly like added files (the
 * parent mounts a fresh dialog, with a new key, for each drop).
 */
const BatchUploadDocumentDialog = ({
  open,
  disciplines = [],
  disciplinesLoading,
  onClose,
  onUploadAll,
  initialFiles = [],
  initialDocumentTypeId = DOCUMENT_TYPE_OPTIONS[0].id,
}) => {
  const [rows, setRows] = useState(() =>
    rowsForFiles(initialFiles, initialDocumentTypeId, 0)
  );
  const [disciplineId, setDisciplineId] = useState('');
  // Also the default documentTypeId for any file added from this point on
  // -- see this component's own module docstring for why.
  const [bulkTypeId, setBulkTypeId] = useState(initialDocumentTypeId);
  const [batchUploading, setBatchUploading] = useState(false);
  const nextRowIdRef = useRef(initialFiles.length);

  const resetAndClose = () => {
    setRows([]);
    setDisciplineId('');
    setBulkTypeId(DOCUMENT_TYPE_OPTIONS[0].id);
    setBatchUploading(false);
    onClose();
  };

  const handleFilesSelected = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const newRows = rowsForFiles(files, bulkTypeId, nextRowIdRef.current);
    nextRowIdRef.current += files.length;
    setRows((prev) => [...prev, ...newRows]);
  };

  const handleRemove = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleTitleChange = (id, title) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, title } : row))
    );
  };

  const handleTypeChange = (id, documentTypeId) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, documentTypeId } : row))
    );
  };

  // Bulk "assign type to all" -- applies documentTypeId to every row
  // CURRENTLY in the batch (regardless of status), and becomes the default
  // for any file added afterward. A row's own Type Select remains
  // independently editable after this -- this only sets each row's current
  // value, it does not pin rows to the bulk choice going forward.
  const handleBulkTypeChange = (documentTypeId) => {
    setBulkTypeId(documentTypeId);
    setRows((prev) => prev.map((row) => ({ ...row, documentTypeId })));
  };

  const eligibleRows = rows.filter(isRowEligibleToUpload);
  const canUploadAll =
    eligibleRows.length > 0 && Boolean(disciplineId) && !batchUploading;

  const handleUploadAllClick = async () => {
    if (!canUploadAll) return;
    const idsToAttempt = new Set(eligibleRows.map((row) => row.id));
    setBatchUploading(true);
    setRows((prev) =>
      prev.map((row) =>
        idsToAttempt.has(row.id)
          ? { ...row, status: 'uploading', errorMessage: '' }
          : row
      )
    );

    const items = eligibleRows.map((row) => ({
      id: row.id,
      file: row.file,
      title: row.title.trim(),
      documentTypeId: row.documentTypeId,
      disciplineId,
    }));
    const othersAlreadyUploaded = rows.every(
      (row) => idsToAttempt.has(row.id) || row.status === 'success'
    );
    const succeededIds = new Set();

    await onUploadAll(items, (item, outcome) => {
      if (outcome.success) succeededIds.add(item.id);
      setRows((prev) =>
        prev.map((row) => {
          if (row.id !== item.id) return row;
          return outcome.success
            ? { ...row, status: 'success', errorMessage: '' }
            : {
                ...row,
                status: 'error',
                errorMessage: outcome.errorMessage || 'Upload failed.',
              };
        })
      );
    });

    if (othersAlreadyUploaded && succeededIds.size === items.length) {
      resetAndClose();
      return;
    }
    setBatchUploading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={batchUploading ? undefined : resetAndClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Upload documents</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl
              size="small"
              fullWidth
              disabled={disciplinesLoading || batchUploading}
            >
              <InputLabel id="batch-upload-discipline-label">
                Discipline
              </InputLabel>
              <Select
                labelId="batch-upload-discipline-label"
                label="Discipline"
                value={disciplineId}
                onChange={(e) => setDisciplineId(e.target.value)}
              >
                {disciplines.map((discipline) => (
                  <MenuItem key={discipline.id} value={discipline.id}>
                    {discipline.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth disabled={batchUploading}>
              <InputLabel id="batch-upload-bulk-type-label">
                Assign type to all
              </InputLabel>
              <Select
                labelId="batch-upload-bulk-type-label"
                label="Assign type to all"
                value={bulkTypeId}
                onChange={(e) => handleBulkTypeChange(e.target.value)}
              >
                {DOCUMENT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button component="label" variant="outlined" size="small">
            Add files
            <input
              type="file"
              hidden
              multiple
              accept=".pdf,.docx"
              onChange={(e) => {
                handleFilesSelected(e.target.files);
                // Allow re-selecting the same file(s) later without the
                // browser silently ignoring an unchanged file list.
                e.target.value = '';
              }}
            />
          </Button>

          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No files selected yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {rows.map((row) => (
                <Box
                  key={row.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    p: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.file.name}
                    >
                      {row.file.name}
                    </Typography>
                    {row.status === 'uploading' && (
                      <CircularProgress size={14} />
                    )}
                    {row.status === 'success' && (
                      <Chip size="small" color="success" label="Uploaded" />
                    )}
                    <IconButton
                      size="small"
                      aria-label={`Remove ${row.file.name}`}
                      onClick={() => handleRemove(row.id)}
                      disabled={row.status === 'uploading'}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      label="Title"
                      value={row.title}
                      onChange={(e) =>
                        handleTitleChange(row.id, e.target.value)
                      }
                      disabled={row.status === 'uploading'}
                      size="small"
                      fullWidth
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id={`batch-upload-type-label-${row.id}`}>
                        Type
                      </InputLabel>
                      <Select
                        labelId={`batch-upload-type-label-${row.id}`}
                        label="Type"
                        value={row.documentTypeId}
                        disabled={row.status === 'uploading'}
                        onChange={(e) =>
                          handleTypeChange(row.id, e.target.value)
                        }
                      >
                        {DOCUMENT_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {row.errorMessage && (
                    <Typography variant="caption" color="error">
                      {row.errorMessage}
                    </Typography>
                  )}
                  {!row.invalid && !row.title.trim() && (
                    <Typography variant="caption" color="error">
                      Title is required.
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} disabled={batchUploading}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleUploadAllClick}
          disabled={!canUploadAll}
          startIcon={batchUploading ? <CircularProgress size={16} /> : null}
        >
          {batchUploading ? 'Uploading...' : 'Upload All'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchUploadDocumentDialog;
